import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function SellDialog({ product, onClose, onSold }) {
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(product?.selling_price ?? "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (qty < 1) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/products/${product.id}/sell`, {
        quantity: qty,
        unit_price: price === "" || price == null ? undefined : Number(price),
      });
      toast.success(`Sale recorded${data.new_stock !== null ? ` · ${data.new_stock} left` : ""}`);
      onSold?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record sale");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 z-50 bg-black/40"
          onClick={onClose}
          data-testid="sell-dialog-overlay"
        >
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 260 }}
            className="absolute bottom-0 inset-x-0 rounded-t-3xl p-6 pb-8"
            style={{ background: "var(--paper)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[9px] tracking-editorial uppercase" style={{ color: "var(--ink-3)" }}>Record sale</div>
                <h3 className="font-display text-[22px] leading-tight mt-1">{product.name}</h3>
              </div>
              <button
                data-testid="sell-close"
                onClick={onClose}
                className="w-8 h-8 rounded-full grid place-items-center press"
              >
                <X size={14} />
              </button>
            </div>

            <div className="mt-5 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  data-testid="sell-qty-minus"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-9 h-9 rounded-full grid place-items-center press"
                  style={{ border: "1px solid rgba(27,25,23,0.15)" }}
                >
                  <Minus size={14} />
                </button>
                <div
                  data-testid="sell-qty-value"
                  className="font-display text-2xl w-10 text-center"
                >{qty}</div>
                <button
                  data-testid="sell-qty-plus"
                  onClick={() => setQty((q) => q + 1)}
                  className="w-9 h-9 rounded-full grid place-items-center press"
                  style={{ border: "1px solid rgba(27,25,23,0.15)" }}
                >
                  <Plus size={14} />
                </button>
                <div className="text-[10px] tracking-editorial-sm uppercase ml-1" style={{ color: "var(--ink-3)" }}>Qty</div>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-[9px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>Sold at price (₹)</label>
              <input
                data-testid="sell-price-input"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={`Default: ₹${Math.round(product.selling_price || 0)}`}
                className="mt-1 w-full h-11 rounded-xl px-3 text-sm outline-none"
                style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.15)" }}
              />
              <div className="text-[10px] mt-1" style={{ color: "var(--ink-3)" }}>Leave as-is to use the listed price</div>
            </div>

            {typeof product.stock_quantity === "number" && (
              <div className="mt-3 text-[11px]" style={{ color: "var(--ink-2)" }}>
                Current stock: <strong>{product.stock_quantity}</strong> → will become <strong>{Math.max(0, product.stock_quantity - qty)}</strong>
              </div>
            )}

            <button
              data-testid="sell-confirm"
              onClick={submit}
              disabled={saving || qty < 1}
              className="mt-5 w-full h-12 rounded-full text-[12px] tracking-editorial-sm uppercase press disabled:opacity-60"
              style={{ background: "var(--ink)", color: "var(--bg)" }}
            >
              {saving ? "Saving…" : `Record −${qty} sold`}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
